// Lightweight inline-SVG icon set (no icon-library dependency). Stroke-based,
// 24×24, inherits `currentColor` so icons take the button's text color. Add a
// glyph by adding an entry to PATHS.

export type IconName =
  | "arrow-left"
  | "plus"
  | "folder"
  | "folder-open"
  | "file"
  | "upload"
  | "download"
  | "image"
  | "pdf"
  | "present"
  | "share"
  | "edit"
  | "trash"
  | "move"
  | "search"
  | "more"
  | "chevron-right"
  | "chevron-down"
  | "chevron-up"
  | "check"
  | "close"
  | "bold"
  | "italic"
  | "underline"
  | "list-ul"
  | "list-ol"
  | "link"
  | "undo"
  | "redo"
  | "clear-format"
  | "sign-out"
  | "copy"
  | "align-left"
  | "align-center"
  | "align-right"
  | "align-justify"
  | "align-top"
  | "align-middle"
  | "align-bottom"
  | "eye"
  | "shield"
  | "message"
  | "chart";

// Each entry renders the inner SVG content (paths/shapes).
const PATHS: Record<IconName, JSX.Element> = {
  "arrow-left": <path d="M15 5l-7 7 7 7" />,
  plus: <path d="M12 5v14M5 12h14" />,
  folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
  "folder-open": (
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v1H7l-3 8H4a2 2 0 0 1-1-1.7zM7 10h15l-2.5 8a1 1 0 0 1-1 .7" />
  ),
  file: <path d="M7 3h7l5 5v13H7zM14 3v5h5" />,
  upload: <path d="M12 16V4m0 0l-4 4m4-4l4 4M4 18v1a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1" />,
  download: <path d="M12 4v12m0 0l-4-4m4 4l4-4M4 18v1a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1" />,
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="M21 16l-5-5L4 19" />
    </>
  ),
  pdf: (
    <>
      <path d="M7 3h7l5 5v13H7zM14 3v5h5" />
      <path d="M9 13h1.5a1.2 1.2 0 0 1 0 2.4H9zM9 13v4M14 13v4m0-4h2m-2 2h1.6M18.5 13H20m-1.5 0v4" />
    </>
  ),
  present: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="1.5" />
      <path d="M12 16v4m-3 0h6" />
    </>
  ),
  share: (
    <>
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M8.2 10.8l7.6-3.6M8.2 13.2l7.6 3.6" />
    </>
  ),
  edit: <path d="M4 20h4L19 9l-4-4L4 16zM14 6l4 4" />,
  trash: <path d="M4 7h16M9 7V4h6v3m-8 0l1 13h8l1-13" />,
  move: <path d="M12 3v18M3 12h18M12 3l-3 3m3-3l3 3M12 21l-3-3m3 3l3-3M3 12l3-3m-3 3l3 3M21 12l-3-3m3 3l-3 3" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M20 20l-4-4" />
    </>
  ),
  more: (
    <>
      <circle cx="5" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="19" cy="12" r="1.4" />
    </>
  ),
  "chevron-right": <path d="M9 6l6 6-6 6" />,
  "chevron-down": <path d="M6 9l6 6 6-6" />,
  "chevron-up": <path d="M6 15l6-6 6 6" />,
  check: <path d="M5 13l4 4L19 7" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  bold: <path d="M7 5h6a3.5 3.5 0 0 1 0 7H7zm0 7h7a3.5 3.5 0 0 1 0 7H7z" />,
  italic: <path d="M10 5h7M7 19h7M14 5l-4 14" />,
  underline: <path d="M7 4v6a5 5 0 0 0 10 0V4M5 21h14" />,
  "list-ul": (
    <>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <circle cx="4.5" cy="6" r="1" />
      <circle cx="4.5" cy="12" r="1" />
      <circle cx="4.5" cy="18" r="1" />
    </>
  ),
  "list-ol": <path d="M10 6h10M10 12h10M10 18h10M4 5l1-.5V9M4 16h2l-2 2.5h2" />,
  link: <path d="M9 15l6-6M10 6l1-1a3.5 3.5 0 0 1 5 5l-1 1M14 18l-1 1a3.5 3.5 0 0 1-5-5l1-1" />,
  undo: <path d="M9 7L4 12l5 5M4 12h11a5 5 0 0 1 0 10h-2" />,
  redo: <path d="M15 7l5 5-5 5M20 12H9a5 5 0 0 0 0 10h2" />,
  "clear-format": <path d="M6 5h12M9 5l-2 14M13 5l-1 7M5 19l14-14" />,
  "sign-out": <path d="M14 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-2M9 12h11m0 0l-3-3m3 3l-3 3" />,
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
    </>
  ),
  "align-left": <path d="M4 6h16M4 12h10M4 18h13" />,
  "align-center": <path d="M4 6h16M7 12h10M6 18h12" />,
  "align-right": <path d="M4 6h16M10 12h10M7 18h13" />,
  "align-justify": <path d="M4 6h16M4 12h16M4 18h16" />,
  "align-top": <path d="M4 4h16M9 8v11M15 8v7" />,
  "align-middle": <path d="M4 12h16M9 7v10M15 9v6" />,
  "align-bottom": <path d="M4 20h16M9 5v11M15 9v7" />,
  eye: (
    <>
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="2.5" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3l7 3v5c0 4.5-3.1 7.4-7 8.9C8.1 18.4 5 15.5 5 11V6z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  message: <path d="M20 4H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3v4l5-4h8a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1z" />,
  chart: (
    <>
      <path d="M4 20h16" />
      <path d="M7 20v-6M12 20V6M17 20v-9" />
    </>
  ),
};

interface Props {
  name: IconName;
  size?: number;
  className?: string;
  title?: string;
}

export function Icon({ name, size = 20, className, title }: Props) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}
